import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useParams, useNavigate, Link } from 'react-router-dom'
import DatePicker, { registerLocale } from 'react-datepicker'
import ptBR from 'date-fns/locale/pt-BR'
import 'react-datepicker/dist/react-datepicker.css' // Import datepicker styles
import { FaTicketAlt } from 'react-icons/fa'
import { useAuth, useWallet } from '@contexts'
import { formatCurrency, formatCPF, formatDateToDayMonthYear, formatDateToISO, formatDateTime, parseDayMonthYearToDate, parseDate } from '@utils/formatters'
import { validateCPF, validateCardExpiry } from '@utils/validators'
import { copyToClipboard } from '@utils/clipboard'
import { Button, Loading, Modal, Badge, Dropdown, DatePickerInput } from '@components'
import api from '@services/api'
import { MetaPixel } from '@services/metaPixel'
import { useToast } from '@contexts/ToastContext'
import './Checkout.css'
import { useCityContext } from '@contexts/LocationContext'
import { ENFORCE_FIRST_TICKET_OWNER, ENABLE_TICKET_EMAIL_NOTIFICATIONS, ENABLE_CHECKOUT_BENEFICIARY_SUMMARY, DISABLE_PASSPORT_REGISTRATION } from '@config/features'
import { DOCUMENT_TYPES } from '@config/constants'

registerLocale('pt-BR', ptBR)




// Métodos de pagamento (types: "Pix", "CreditCard", "Balance")
const PAYMENT_METHODS = [
  { id: 'pix', name: 'Pix', type: 'Pix', icon: '💳', description: 'Aprovação instantânea' },
  { id: 'credit', name: 'Cartão de Crédito', type: 'CreditCard', icon: '💳', description: 'Cartão de crédito' },
  { id: 'balance', name: 'Saldo na Plataforma', type: 'Balance', icon: '💰', description: 'Use seu saldo disponível' }
]

const CARD_BRANDS = [
  { value: 'Visa', label: 'Visa' },
  { value: 'Mastercard', label: 'Mastercard' },
  { value: 'Amex', label: 'Amex' },
  { value: 'Elo', label: 'Elo' },
  { value: 'Aura', label: 'Aura' },
  { value: 'JCB', label: 'JCB' },
  { value: 'Diners', label: 'Diners' },
  { value: 'Discover', label: 'Discover' }
]

const Checkout = () => {
  const { eventId } = useParams()
  const { city } = useCityContext()
  const navigate = useNavigate()
  const {
    user,
    isAuthenticated,
    loading: authLoading,
    isRegistrationComplete,
    canPurchaseTickets,
    refreshProfile
  } = useAuth()
  const { loadAll } = useWallet()
  const { showToast } = useToast()
  const [step, setStep] = useState(1) // 1: Participantes, 2: Pagamento, 3: Confirmação
  const [event, setEvent] = useState(null)
  const sortedTicketTypes = useMemo(() => {
    if (!event?.ticket_types) return []
    return [...event.ticket_types].sort((a, b) => a.value_cents - b.value_cents)
  }, [event?.ticket_types])
  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState(false)
  const [extraQuestions, setExtraQuestions] = useState([])

  // Quantidade total de ingressos a serem comprados (definida no checkout)
  const [totalTicketsToPurchase, setTotalTicketsToPurchase] = useState(1);

  // Dados dos participantes
  const [participants, setParticipants] = useState([])
  const [participantErrors, setParticipantErrors] = useState({})

  // Pagamento
  const [paymentMethod, setPaymentMethod] = useState('pix')
  const [cardData, setCardData] = useState({
    number: '',
    name: '',
    expiry: '',
    cvv: '',
    brand: ''
  })

  // Saldo do usuário e confirmação
  const [isFullBalancePurchase, setIsFullBalancePurchase] = useState(false)

  // Resultado
  const [orderResult, setOrderResult] = useState(null)
  const [showPixModal, setShowPixModal] = useState(false)
  const [showExistingSaleModal, setShowExistingSaleModal] = useState(false);
  const [existingSaleData, setExistingSaleData] = useState(null);
  const [pixData, setPixData] = useState(null) // { qr_code, qr_code_base64 }

  // Função para processar a data de nascimento do usuário logado de forma robusta
  const getUserBirthDate = useCallback((u) => {
    if (!u?.birth_date) return '';

    // O formatador formatDateToDayMonthYear já lida com strings UTC e objetos Date
    // convertendo-os para o timezone local do usuário.
    return formatDateToDayMonthYear(u.birth_date);
  }, []);

  // State for sale and countdown
  const [saleId, setSaleId] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0); // in seconds
  const timerRef = useRef(null);

  const userBalanceCents = user?.credit_cents || 0;
  const userBalance = userBalanceCents / 100;

  // Coupon state
  const [coupon, setCoupon] = useState(null);
  const [couponInput, setCouponInput] = useState('');
  const [couponError, setCouponError] = useState('');
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);

  // Calcula totais (declarados no topo para evitar use-before-define)
  const getSubtotal = useCallback(() => {
    const subtotal = participants.reduce((sum, p) => {
      let price = p.ticketPrice;
      if (coupon && coupon.discount > 0) {
        const priceCents = Math.round(p.ticketPrice * 100);
        const discountedPriceCents = Math.round(priceCents * (100 - coupon.discount) / 100);
        price = discountedPriceCents / 100;
      }
      return sum + price;
    }, 0);
    return subtotal;
  }, [participants, coupon]);

  const getServiceFee = useCallback(() => {
    return 0; // Taxa de serviço removida
  }, []);

  const getDiscountValue = useCallback(() => {
    if (!coupon || coupon.discount <= 0) return 0;
    const subtotalWithoutCoupon = participants.reduce((sum, p) => sum + p.ticketPrice, 0);
    return subtotalWithoutCoupon - getSubtotal();
  }, [coupon, participants, getSubtotal]);

  const getTotal = useCallback(() => {
    const total = getSubtotal() + getServiceFee();
    return total;
  }, [getSubtotal, getServiceFee]);

  const maxAllowed = useMemo(() => {
    if (!event) return undefined; // Undefined before load

    // Total allowed calculation
    let extraTicketsAllowed;

    if (coupon && coupon.coupon_type === 'BIRTHDAY') {
      // For birthday coupons, extra tickets is unique_usage_limit - 1
      const limit = coupon.unique_usage_limit || coupon.usage_limit || 1;
      extraTicketsAllowed = Math.max(0, limit - 1);
    } else {
      // Default logic: event.max_tickets is the number of EXTRA tickets allowed
      extraTicketsAllowed = (event.max_tickets !== undefined && event.max_tickets !== null) ? event.max_tickets : 2;
    }

    const totalAllowed = 1 + extraTicketsAllowed;
    const remaining = event.total_stock - event.sold_stock;

    if (remaining <= 0) return 0;
    if (isNaN(remaining)) return totalAllowed;
    return Math.min(totalAllowed, remaining);
  }, [event, coupon]);

  useEffect(() => {
    if (maxAllowed !== undefined) {
      if (totalTicketsToPurchase > maxAllowed) {
        setTotalTicketsToPurchase(maxAllowed);
      }
      if (totalTicketsToPurchase === 0 && maxAllowed > 0) {
        setTotalTicketsToPurchase(1);
      }
    }
  }, [maxAllowed, totalTicketsToPurchase]);

  // Function to calculate time left
  const calculateTimeLeft = useCallback((expirationTime) => {
    const now = new Date().getTime();
    const difference = parseDate(expirationTime).getTime() - now;
    return Math.max(0, Math.floor(difference / 1000));
  }, []);

  // Format time for display
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Redireciona se não autenticado ou sem cadastro completo
  useEffect(() => {
    if (authLoading) return

    if (!isAuthenticated) {
      navigate(`/${city?.slug || ''}/login`, { state: { from: { pathname: `/${city?.slug || ''}/checkout/${eventId}` } } })
      return
    }

    if (!canPurchaseTickets) {
      // Se o cadastro estiver incompleto, vai para completar-cadastro
      if (!isRegistrationComplete) {
        navigate(`/${city?.slug || ''}/completar-cadastro`, { state: { from: { pathname: `/${city?.slug || ''}/checkout/${eventId}` } } })
        return
      }

      // Se o e-mail não estiver verificado ou tiver senha temporária, volta para o evento
      // O Layout já mostrará o warning explicando o motivo
      navigate(`/${city?.slug || ''}/evento/${eventId}`)
      return
    }
  }, [authLoading, isAuthenticated, canPurchaseTickets, navigate, eventId, city, isRegistrationComplete])

  // Refs for tracking Meta Pixel events only once per checkout session
  const initiatedCheckoutRef = useRef(false);
  const addedPaymentInfoRef = useRef(false);

  // Track InitiateCheckout (Step 1)
  useEffect(() => {
    if (event && event.meta_pixel_id && step === 1 && !initiatedCheckoutRef.current) {
      const userData = {};
      if (user) {
        if (user.email) userData.email = user.email;
        if (user.phone) userData.phone = user.phone;
        if (user.name) userData.name = user.name;
      }
      
      MetaPixel.init(event.meta_pixel_id, userData);
      MetaPixel.track(event.meta_pixel_id, 'InitiateCheckout', {
        content_name: event.name,
        content_category: event.event_type,
        content_ids: [event.event_id],
        content_type: 'product',
        value: getTotal(),
        currency: 'BRL',
        num_items: totalTicketsToPurchase
      });
      initiatedCheckoutRef.current = true;
    }
  }, [event, step, user, totalTicketsToPurchase, getTotal]);

  // Track AddPaymentInfo (Step 2)
  useEffect(() => {
    if (event && event.meta_pixel_id && step === 2 && !addedPaymentInfoRef.current) {
      const userData = {};
      if (user) {
        if (user.email) userData.email = user.email;
        if (user.phone) userData.phone = user.phone;
        if (user.name) userData.name = user.name;
      }
      
      MetaPixel.init(event.meta_pixel_id, userData);
      MetaPixel.track(event.meta_pixel_id, 'AddPaymentInfo', {
        content_name: event.name,
        content_category: event.event_type,
        content_ids: [event.event_id],
        content_type: 'product',
        value: getTotal(),
        currency: 'BRL',
        num_items: totalTicketsToPurchase
      });
      addedPaymentInfoRef.current = true;
    }
  }, [event, step, user, totalTicketsToPurchase, getTotal]);

  // Effect to load sale data from API on component mount/refresh
  useEffect(() => {
    const fetchActiveSale = async () => {
      if (!isAuthenticated || !eventId) return;

      try {
        const salesResponse = await api.get('/sales/?page=1&page_size=10');
        const activeSale = salesResponse?.items?.find(s => s.status === 'RESERVED');

        if (activeSale) {
          const { transaction_id, creation_date, event_id, processed_until } = activeSale;

          // Use backend's processed_until, or fallback to +10 minutes of creation_date
          let expiresAt;
          if (processed_until) {
            expiresAt = processed_until;
          } else {
            const expiresAtDate = parseDate(creation_date);
            expiresAtDate.setMinutes(expiresAtDate.getMinutes() + 10);
            expiresAt = expiresAtDate.toISOString();
          }

          if (calculateTimeLeft(expiresAt) <= 0) {
            return;
          }

          if (event_id === eventId) {
            // It's for the current event, resume it
            setSaleId(transaction_id);
            setExpiresAt(expiresAt);
            setTimeLeft(calculateTimeLeft(expiresAt));

            // Fetch full sale details to restore participants
            try {
              const fullSale = await api.get(`/sales/${transaction_id}`);
              if (fullSale && fullSale.tickets) {
                const restoredParticipants = fullSale.tickets.map((t, i) => {
                  // Tenta buscar o preço em diferentes locais possíveis do objeto
                  const valueCents = t.event_ticket?.value_cents ?? t.ticket_type?.value_cents ?? 0;
                  const price = valueCents / 100;

                  return {
                    id: `participant-${i}-${Date.now()}`,
                    ticketId: t.event_ticket?.id || t.ticket_type?.id,
                    ticketName: t.event_ticket?.name || t.ticket_type?.name || 'Ingresso',
                    ticketPrice: price,
                    documentType: t.owner?.document_type || 'CPF',
                    document: t.owner?.main_document || '',
                    name: t.owner?.name || '',
                    dob: t.owner?.birth_date ? formatDateToDayMonthYear(t.owner.birth_date) : '',
                    isOwner: t.owner?.id && user?.id ? t.owner.id === user.id : false,
                    user_id: t.owner?.id,
                    verificationStatus: 'success',
                    verificationMessage: 'Usuário verificado.',
                    lastVerifiedData: null
                  };
                });

                setParticipants(restoredParticipants);
                setTotalTicketsToPurchase(restoredParticipants.length);

                // Pequeno delay para garantir que o estado dos participantes foi processado
                setTimeout(() => {
                  setStep(2);
                }, 100);
              }
            } catch (err) {
              console.error('Failed to fetch full sale data:', err);
              showToast('Não foi possível restaurar os dados da venda. Por favor, reinicie.', 'error');
              setStep(1);
            }
          } else {
            // Stored sale is for a DIFFERENT event.
            // Show modal to ask user what to do.
            setExistingSaleData({
              saleId: transaction_id,
              eventId: event_id,
              eventName: activeSale.event?.name
            });
            setShowExistingSaleModal(true);
          }
        }
      } catch (e) {
        console.error('Failed to fetch active sale data:', e);
      }
    };

    fetchActiveSale();
  }, [isAuthenticated, eventId, loading, calculateTimeLeft, user?.id, showToast]);

  // Handle cancelling a sale (API)
  const handleCancelSale = async (idToCancel) => {
    const id = idToCancel || saleId;
    if (!id) return;

    try {
      await api.delete(`/sales/${id}`);
    } catch (error) {
      console.warn('Failed to cancel sale on API (might be already expired/cancelled):', error);
    } finally {
      setSaleId(null);
      setExpiresAt(null);
      setTimeLeft(0);
      setStep(1);
    }
  };

  const handleResumeExistingSale = () => {
    if (!existingSaleData) return;
    const { eventId: otherEventId } = existingSaleData;
    navigate(`/${city?.slug || ''}/checkout/${otherEventId}`);
    setShowExistingSaleModal(false);
  };

  const handleDiscardExistingSale = async () => {
    if (!existingSaleData) return;
    await handleCancelSale(existingSaleData.saleId);
    setExistingSaleData(null);
    setShowExistingSaleModal(false);
  };


  // Effect for the countdown timer
  useEffect(() => {
    if (!expiresAt || loading || step !== 2) return; // Only run timer if on step 2, not loading, and expiresAt is set

    timerRef.current = setInterval(() => {
      const newTimeLeft = calculateTimeLeft(expiresAt);
      setTimeLeft(newTimeLeft);

      if (newTimeLeft <= 0) {
        clearInterval(timerRef.current);
        showToast('O tempo para finalizar a compra expirou. Por favor, tente novamente.', 'error');
        setSaleId(null);
        setExpiresAt(null);
        setTimeLeft(0);
        setShowPixModal(false);
        setPixData(null);
        setStep(1); // Go back to participants step
      }
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [expiresAt, loading, step, showToast, calculateTimeLeft]);



  // Carrega evento
  useEffect(() => {
    const loadEvent = async () => {
      setLoading(true)
      try {
        const data = await api.get(`/events/${eventId}`)
        setEvent(data)

        // Fetch extra questions if the event has them
        if (data.has_extra_questions) {
          try {
            const questions = await api.get(`/events/${eventId}/questions`);
            // Inicializa cada pergunta com um campo 'answer' e limpa erros
            setExtraQuestions(questions.map(q => ({ ...q, answer: '', error: null })));
          } catch (qError) {
            console.error('Erro ao carregar perguntas extras:', qError);
          }
        }
      } catch (error) {
        console.error('Erro ao carregar evento:', error)
        setEvent(null) // Ensure event is null on error
      } finally {
        setLoading(false)
      }
    }

    loadEvent()
  }, [eventId])

  // Adjust participants array when event data or totalTicketsToPurchase changes
  useEffect(() => {
    if (event && step === 1) {
      setParticipants(prevParticipants => {
        const currentTotalTickets = totalTicketsToPurchase;
        const existingCount = prevParticipants.length;
        const newParticipants = [...prevParticipants];

        if (currentTotalTickets > existingCount) {
          // Add new participants
          for (let i = existingCount; i < currentTotalTickets; i++) {
            const isOwner = (i === 0); // Only the first participant is potentially the owner
            const participantDocumentType = isOwner ? (user?.document_type || 'CPF') : 'CPF'
            const participantDocument = isOwner
              ? (user?.main_document ? (participantDocumentType === 'CPF' ? user.main_document.replace(/\D/g, '') : user.main_document) : '')
              : '';

            newParticipants.push({
              id: `participant-${i}-${Date.now()}`, // Unique ID for participant
              ticketId: null, // This will be selected by the user
              ticketName: '', // Derived from ticketId
              ticketPrice: 0, // Derived from ticketId
              documentType: participantDocumentType, // Pre-fill if isOwner
              document: participantDocument, // Pre-fill if isOwner
              name: isOwner ? (user?.name || '') : '',     // Pre-fill if isOwner
              dob: isOwner ? getUserBirthDate(user) : '', // Pre-fill if isOwner
              isOwner: isOwner,
              user_id: isOwner && user ? user.id : null, // Corrected: Use user.id instead of user.uid
              verificationStatus: isOwner ? 'success' : 'pending', // 'pending', 'verifying', 'success', 'error'
              verificationMessage: isOwner ? 'Usuário da conta, verificado automaticamente.' : null,
              lastVerifiedData: null
            });
          }
        } else if (currentTotalTickets < existingCount) {
          // Remove excess participants
          newParticipants.splice(currentTotalTickets);
        }

        // Ensure the first participant always reflects the owner status correctly if user changes
        // This logic will apply if newParticipants[0] exists
        if (newParticipants[0] && user) {
          newParticipants[0].isOwner = true; // Ensure first is owner
          newParticipants[0].name = user?.name || '';
          newParticipants[0].documentType = user?.document_type || 'CPF';
          newParticipants[0].document = user?.main_document ? (newParticipants[0].documentType === 'CPF' ? user.main_document.replace(/\D/g, '') : user.main_document) : '';
          newParticipants[0].dob = getUserBirthDate(user);
          newParticipants[0].user_id = user.id; // Corrected: Use user.id instead of user.uid
          // Update new fields for owner
          newParticipants[0].verificationStatus = 'success'; // Owner is implicitly verified
          newParticipants[0].verificationMessage = 'Usuário da conta, verificado automaticamente.';
        } else if (newParticipants[0] && !user) {
          newParticipants[0].isOwner = false;
          // Also reset verification status for non-owner
          newParticipants[0].verificationStatus = 'pending';
          newParticipants[0].verificationMessage = null;
          newParticipants[0].user_id = null; // Clear user_id if not an owner or no user
        }


        return newParticipants;
      });
    }
  }, [event, totalTicketsToPurchase, user, getUserBirthDate, step]);

  const participantsRef = useRef(participants);
  useEffect(() => {
    participantsRef.current = participants;
  }, [participants]);

  // Effect to verify non-owner participants
  useEffect(() => {
    if (step !== 1) return; // Only verify if we are in participants step

    // Debounce verification calls
    const handler = setTimeout(() => {
      const currentParticipants = participantsRef.current;

      currentParticipants.forEach(async (p, index) => {
        if (p.isOwner) return;

        const isCpf = p.documentType?.toUpperCase() === 'CPF';
        const docLen = p.document?.length || 0;

        const isDataComplete = isCpf
          ? docLen === 11
          : (docLen >= 5 && p.name && p.name.length >= 3 && p.dob && p.dob.length === 10);

        // Prepare current data snapshot for comparison
        const currentParticipantData = {
          document: p.document,
          documentType: p.documentType,
          name: isCpf ? undefined : p.name,
          dob: isCpf ? undefined : p.dob
        };

        const dataChanged = !p.lastVerifiedData || JSON.stringify(currentParticipantData) !== JSON.stringify(p.lastVerifiedData);
        const alreadyVerifying = p.verificationStatus === 'verifying';
        const isPersistentError = p.verificationStatus === 'error' && !dataChanged;

        // Remove local validateCPF from trigger condition to allow API to return formal error message
        const needsVerification = dataChanged && isDataComplete && !alreadyVerifying && !isPersistentError;

        // Check for duplicates within ALL participants
        const isDuplicate = currentParticipants.some((otherP, otherIndex) =>
          otherIndex !== index &&
          otherP.document &&
          p.document &&
          otherP.document.replace(/\D/g, '') === p.document.replace(/\D/g, '') &&
          otherP.documentType?.toUpperCase() === p.documentType?.toUpperCase()
        );

        if (isDuplicate && p.document && isDataComplete) {
          setParticipants(prev => {
            const updated = [...prev];
            if (updated[index].id !== p.id) return prev;
            // Clear the input and reset verification
            updated[index] = {
              ...updated[index],
              document: '',
              verificationStatus: 'pending',
              verificationMessage: null,
              lastVerifiedData: null
            };
            return updated;
          });

          setParticipantErrors(prev => ({
            ...prev,
            [`${index}-document`]: 'Este documento já foi utilizado.'
          }));
          return;
        }

        if (!p.isOwner && needsVerification) {
          setParticipants(prev => {
            const updated = [...prev];
            if (updated[index].id !== p.id) return prev;
            updated[index] = { ...updated[index], verificationStatus: 'verifying', verificationMessage: 'Verificando dados do usuário...' };
            return updated;
          });

          try {
            const userData = {
              document_type: p.documentType,
              main_document: p.document,
            };

            if (!isCpf) {
              userData.name = p.name;
              const dateObj = parseDayMonthYearToDate(p.dob);
              if (dateObj) {
                userData.birth_date = formatDateToISO(dateObj);
              }
            }

            const result = await api.checkOrCreateUser(userData);

            setParticipants(prev => {
              const updated = [...prev];
              if (updated[index].id !== p.id) return prev;
              if (result.success && result.data && result.data.user && result.data.user.id) {
                updated[index] = {
                  ...updated[index],
                  verificationStatus: 'success',
                  verificationMessage: 'Usuário verificado com sucesso.',
                  lastVerifiedData: currentParticipantData,
                  user_id: result.data.user.id,
                  name: result.data.user.name || updated[index].name,
                  dob: result.data.user.birth_date ? formatDateToDayMonthYear(result.data.user.birth_date) : updated[index].dob,
                };
              } else {
                const message = result.message || "Não foi possível verificar os dados.";
                updated[index] = { ...updated[index], verificationStatus: 'error', verificationMessage: message, lastVerifiedData: currentParticipantData, user_id: null };
              }
              return updated;
            });
          } catch (error) {
            console.error('Verification error:', error);
            setParticipants(prev => {
              const updated = [...prev];
              if (updated[index].id !== p.id) return prev;
              updated[index] = { ...updated[index], verificationStatus: 'error', verificationMessage: error.message || "Erro de conexão", lastVerifiedData: currentParticipantData, user_id: null };
              return updated;
            });
          }

        } else if (dataChanged && !isDataComplete && p.verificationStatus !== 'pending') {
          // Reset if data became invalid or incomplete
          setParticipants(prev => {
            const updated = [...prev];
            if (updated[index].id !== p.id) return prev;
            updated[index] = { ...updated[index], verificationStatus: 'pending', verificationMessage: null, lastVerifiedData: null, user_id: null };
            return updated;
          });
        }
      });
    }, 500);

    return () => clearTimeout(handler);
  }, [participants, step]);

  // Atualiza dados do participante
  const handleParticipantChange = useCallback((index, field, value) => {
    setParticipants(prev => {
      const updated = [...prev]
      let participantToUpdate = { ...updated[index] };

      if (field === 'documentType') {
        const oldType = participantToUpdate.documentType;
        if (oldType !== value) {
          participantToUpdate.documentType = value;
          // If not owner, clear fields to avoid keeping CPF data in Passport or vice-versa
          if (!participantToUpdate.isOwner) {
            participantToUpdate.document = '';
            participantToUpdate.name = '';
            participantToUpdate.dob = '';
            participantToUpdate.user_id = null;
            participantToUpdate.verificationStatus = 'pending';
            participantToUpdate.verificationMessage = null;
            participantToUpdate.lastVerifiedData = null;
          }
        }
      } else if (field === 'ticketId') {
        const selectedTicketType = event.ticket_types.find(t => t.id === value);
        if (selectedTicketType) {
          participantToUpdate.ticketId = value;
          participantToUpdate.ticketName = selectedTicketType.name;
          participantToUpdate.ticketPrice = selectedTicketType.value_cents / 100;
        } else {
          // If no ticket type is selected or found, reset related fields
          participantToUpdate.ticketId = null;
          participantToUpdate.ticketName = '';
          participantToUpdate.ticketPrice = 0;
        }
      } else if (field === 'isOwner' && index === 0) { // Only allow changing for the first participant
        const isCurrentlyOwner = participantToUpdate.isOwner;
        if (!isCurrentlyOwner && value === true) { // Becoming owner (should only happen if somehow deselected)
          participantToUpdate.isOwner = true;
          participantToUpdate.name = user?.name || '';
          participantToUpdate.documentType = user?.document_type || 'CPF'; // Pre-fill document type
          participantToUpdate.document = user?.main_document ? (participantToUpdate.documentType === 'CPF' ? user.main_document.replace(/\D/g, '') : user.main_document) : '';
          participantToUpdate.dob = user?.birth_date ? formatDateToDayMonthYear(user.birth_date) : '';
        } else if (isCurrentlyOwner && value === false) { // Unmarking owner
          participantToUpdate.isOwner = false;
          participantToUpdate.name = ''; // Clear data
          participantToUpdate.document = '';
          participantToUpdate.dob = '';
        }
      } else {
        // For document field, clean CPF value before storing
        const isCpf = participantToUpdate.documentType?.toUpperCase() === 'CPF';
        if (field === 'document' && isCpf) {
          participantToUpdate[field] = value.replace(/\D/g, '').slice(0, 11);
        } else {
          participantToUpdate[field] = value;
        }
      }

      updated[index] = participantToUpdate;
      return updated;
    });

    // Clear error
    if (participantErrors[`${index}-${field}`]) {
      setParticipantErrors(prev => {
        const updated = { ...prev }
        delete updated[`${index}-${field}`]
        return updated
      })
    }
  }, [event, user, participantErrors]); // Added event, user and participantErrors to dependencies for useCallback

  // Valida participantes e perguntas extras
  const validateParticipantsForm = useCallback(() => {
    const errors = {}
    const assignedTicketCounts = {}; // To track assigned tickets per type

    participants.forEach((p, index) => {
      // (mantendo lógica de participantes inalterada...)
      if (!p.name || p.name.trim().length < 3) {
        errors[`${index}-name`] = 'Nome é obrigatório'
      }

      if (p.documentType === 'CPF') {
        if (!p.document || !validateCPF(p.document)) {
          errors[`${index}-document`] = 'CPF inválido'
        }
      } else {
        if (!p.document || p.document.length < 5) {
          errors[`${index}-document`] = 'Passaporte inválido'
        }
      }

      // Check for duplicate document
      const isDuplicate = participants.some((otherP, otherIndex) =>
        otherIndex !== index &&
        otherP.document &&
        p.document &&
        otherP.document.replace(/\D/g, '') === p.document.replace(/\D/g, '') &&
        otherP.documentType?.toUpperCase() === p.documentType?.toUpperCase()
      );
      if (isDuplicate) {
        errors[`${index}-document`] = 'Este documento já foi utilizado.'
      }

      if (!p.dob) { // Basic validation: check if date of birth is provided
        errors[`${index}-dob`] = 'Data de nascimento é obrigatória'
      } else if (!/^\d{2}\/\d{2}\/\d{4}$/.test(p.dob)) {
        errors[`${index}-dob`] = 'Formato inválido (DD/MM/AAAA)'
      }

      // New validation for ticketId
      if (!p.ticketId) {
        errors[`${index}-ticketId`] = 'Selecione o tipo de ingresso';
      } else {
        // Increment count for the assigned ticket type
        assignedTicketCounts[p.ticketId] = (assignedTicketCounts[p.ticketId] || 0) + 1;
      }

      // Ensure user_id is present and verification is successful
      if (!p.user_id || p.verificationStatus !== 'success') {
        errors[`${index}-verification`] = 'Verificação pendente ou falhou. Revise os dados.';
      }
    });

    // Validar perguntas extras se houver
    if (extraQuestions.length > 0) {
      const hasErrors = extraQuestions.some(q => !q.answer || !q.answer.trim());
      if (hasErrors) {
        errors.extraQuestions = 'Por favor, responda a todas as perguntas obrigatórias.';
      }
    }

    const totalAssignedTickets = Object.values(assignedTicketCounts).reduce((sum, count) => sum + count, 0);
    if (totalAssignedTickets !== totalTicketsToPurchase) {
      errors.ticketAssignment = 'Selecione os ingressos para continuar';
    }

    return errors;
  }, [participants, totalTicketsToPurchase, extraQuestions]);

  // Helper para lidar com mudanças nas respostas das perguntas
  const handleQuestionAnswerChange = (questionId, value) => {
    setExtraQuestions(prev => prev.map(q => {
      const qId = q.event_question_id || q.question_id;
      if (qId === questionId) {
        return { ...q, answer: value, error: null };
      }
      return q;
    }));
  };

  // Helper to apply validation errors to state
  const applyParticipantErrors = useCallback((errors) => {
    setParticipantErrors(errors);
  }, []);

  // Calculate if the participant form is valid
  const isParticipantsFormValid = useCallback(() => {
    const errors = validateParticipantsForm();
    const isValid = Object.keys(errors).length === 0;
    const canProceed = isValid && !processing;
    return canProceed; // Ensure no ongoing verification processes
  }, [processing, validateParticipantsForm]);

  const getMissingFieldsMessage = useCallback(() => {
    const errors = validateParticipantsForm();
    const errorKeys = Object.keys(errors);
    if (errorKeys.length === 0) return null;

    if (errors.ticketAssignment) return errors.ticketAssignment;

    // Prioritize specific validation errors like "Passaporte inválido", "CPF inválido", "Data inválida"
    const specificErrorKey = errorKeys.find(key =>
      !key.includes('verification') &&
      key !== 'ticketAssignment'
    );

    if (specificErrorKey) {
      return errors[specificErrorKey];
    }

    const hasVerificationErrors = errorKeys.some(key => key.includes('verification'));
    const hasEmptyFields = errorKeys.some(key => {
      const pIndex = key.split('-')[0];
      if (isNaN(pIndex)) return false;
      const p = participants[pIndex];
      return !p.name || !p.document || !p.dob || !p.ticketId;
    });

    if (hasVerificationErrors && !hasEmptyFields) return 'Aguardando verificação dos dados.';
    if (hasEmptyFields) return 'Preencha todos os campos obrigatórios de todos os ingressos.';

    return 'Revise os dados dos participantes.';
  }, [validateParticipantsForm, participants]);

  // Valida cupom
  const handleValidateCoupon = async () => {
    if (!couponInput.trim()) return;

    setIsValidatingCoupon(true);
    setCouponError('');

    try {
      const response = await api.get(`/me/coupons/${couponInput.trim()}/validate`, { event_id: eventId });
      // response is already jsonResponse.data based on handleResponse in api.js
      setCoupon(response);
      showToast('Cupom aplicado com sucesso!', 'success');
    } catch (error) {
      console.error('Erro ao validar cupom:', error);
      setCouponError(error.message || 'Cupom inválido');
      setCouponInput('');
      setCoupon(null);
    } finally {
      setIsValidatingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setCoupon(null);
    setCouponInput('');
    setCouponError('');
  };

  const handleSuccess = useCallback(async (data, customSaleId = null) => {
    const currentSaleId = customSaleId || saleId;
    try {
      if (event && event.meta_pixel_id) {
        MetaPixel.track(
          event.meta_pixel_id,
          'Purchase',
          {
            value: getTotal(),
            currency: 'BRL',
            content_type: 'product',
            content_name: event.name,
            content_category: event.event_type,
            content_ids: [event.event_id],
            num_items: totalTicketsToPurchase
          },
          { eventID: currentSaleId }
        );
      }
    } catch (err) {
      console.error('Error tracking purchase on Meta Pixel:', err);
    }

    setOrderResult({
      orderId: currentSaleId,
      status: 'confirmed',
      tickets: participants.map((p, index) => ({
        ...p,
        code: data.tickets?.[index]?.code || 'PENDENTE'
      }))
    });
    setStep(3);

    // Refresh wallet data and user profile (for balance) after success
    try {
      await Promise.allSettled([
        loadAll(true),
        refreshProfile()
      ]);
    } catch (error) {
      console.error('Error refreshing data after success:', error);
    }
  }, [saleId, event, totalTicketsToPurchase, participants, loadAll, refreshProfile, getTotal]);

  // Inicia o processo de venda (cria a venda no backend)
  const startSale = async () => {
    setProcessing(true);

    // Check if there is an active sale already via API
    try {
      const salesResponse = await api.get('/sales/?page=1&page_size=10');
      const activeSale = salesResponse?.items?.find(s => s.status === 'RESERVED');

      if (activeSale) {
        const { transaction_id, creation_date, event_id, processed_until } = activeSale;

        // Use backend's processed_until, or fallback to +10 minutes of creation_date
        let expiresAt;
        if (processed_until) {
          expiresAt = processed_until;
        } else {
          const expiresAtDate = parseDate(creation_date);
          expiresAtDate.setMinutes(expiresAtDate.getMinutes() + 10);
          expiresAt = expiresAtDate.toISOString();
        }

        if (calculateTimeLeft(expiresAt) > 0) {
          if (event_id !== eventId) {
            setExistingSaleData({
              saleId: transaction_id,
              eventId: event_id,
              eventName: activeSale.event?.name
            });
            setShowExistingSaleModal(true);
            setProcessing(false);
            return;
          }
          // If it's the same event, just resume it
          setSaleId(transaction_id);
          setExpiresAt(expiresAt);

          // Also restore participants and balance here if it's the same event
          try {
            const fullSale = await api.get(`/sales/${transaction_id}`);
            if (fullSale && fullSale.tickets) {
              const restoredParticipants = fullSale.tickets.map((t, i) => {
                const valueCents = t.event_ticket?.value_cents ?? t.ticket_type?.value_cents ?? 0;
                const price = valueCents / 100;

                return {
                  id: `participant-${i}-${Date.now()}`,
                  ticketId: t.event_ticket?.id || t.ticket_type?.id,
                  ticketName: t.event_ticket?.name || t.ticket_type?.name || 'Ingresso',
                  ticketPrice: price,
                  documentType: t.owner?.document_type || 'CPF',
                  document: t.owner?.main_document || '',
                  name: t.owner?.name || '',
                  dob: t.owner?.birth_date ? formatDateToDayMonthYear(t.owner.birth_date) : '',
                  isOwner: t.owner?.id && user?.id ? t.owner.id === user.id : false,
                  user_id: t.owner?.id,
                  verificationStatus: 'success',
                  verificationMessage: 'Usuário verificado.',
                  lastVerifiedData: null
                };
              });

              setParticipants(restoredParticipants);
              setTotalTicketsToPurchase(restoredParticipants.length);

              setTimeout(() => {
                setStep(2);
                setProcessing(false);
              }, 100);
              return;
            }
          } catch (err) {
            console.error('Error restoring sale in startSale:', err);
          }
          setProcessing(false);
          return;
        }
      }
    } catch (e) {
      console.error('Error checking active sale in startSale:', e);
    }

    // First, validate participants and apply errors if any
    const errors = validateParticipantsForm();
    if (Object.keys(errors).length > 0) {
      // Se houver erros em perguntas extras, atualiza o estado para mostrar as mensagens na UI
      if (errors.extraQuestions) {
        setExtraQuestions(prev => prev.map(q => {
          if (!q.answer || !q.answer.trim()) {
            return { ...q, error: 'Esta pergunta é obrigatória' };
          }
          return { ...q, error: null };
        }));
      }

      applyParticipantErrors(errors);
      showToast('Por favor, corrija os erros nos dados dos participantes para continuar.', 'error');
      setProcessing(false);
      return;
    }

    try {
      const ticketsPayload = participants.map(p => ({
        user_id: p.user_id,
        ticket_type_id: p.ticketId
      }));

      const payload = {
        event_id: eventId,
        tickets: ticketsPayload
      };

      if (extraQuestions.length > 0) {
        payload.question_answers = extraQuestions.map(q => ({
          event_question_id: q.event_question_id || q.question_id,
          answer: q.answer || ''
        }));
      }

      if (coupon) {
        payload.coupon_id = coupon.coupon_id;
      }

      const result = await api.post('/sales/start', payload);
      const { transaction_id, creation_date } = result;

      // Use backend's processed_until, or fallback to +10 minutes of creation_date
      let expiresAt;
      if (result.processed_until) {
        expiresAt = result.processed_until;
      } else {
        const expiresAtDate = parseDate(creation_date);
        expiresAtDate.setMinutes(expiresAtDate.getMinutes() + 10);
        expiresAt = expiresAtDate.toISOString(); // Convert back to ISO string
      }

      setSaleId(transaction_id);
      setExpiresAt(expiresAt);

      const totalValue = getTotal();
      const totalCents = Math.round(totalValue * 100);

      // If total is 0, we can skip payment and go to confirmation
      if (totalValue === 0) {
        try {
          // Fetch final order details to show in Step 3
          const finalOrder = await api.get(`/sales/${transaction_id}`);
          setOrderResult({
            orderId: transaction_id,
            tickets: finalOrder.tickets.map(t => ({
              name: t.owner?.name || 'Participante',
              ticketName: t.event_ticket?.name || t.ticket_type?.name || 'Ingresso'
            }))
          });
          setStep(3);
        } catch (err) {
          console.error('Error fetching final order for zero-cost sale:', err);
          // Fallback to step 2 if we can't get order details
          setStep(2);
        }
      } else if (userBalanceCents >= totalCents && totalCents > 0) {
        // Se o saldo for suficiente, processa o pagamento integral com saldo direto
        try {
          const payments = [{
            type: "Balance",
            amount: totalCents
          }];
          const response = await api.post(`/sales/${transaction_id}/payment`, payments);
          if (response.status === 'COMPLETED') {
            setIsFullBalancePurchase(true);
            await handleSuccess(response, transaction_id);
          } else {
            setStep(2);
          }
        } catch (err) {
          console.error('Erro ao pagar com saldo total:', err);
          showToast(err.message || 'Erro ao processar compra com saldo.', 'error');
          setStep(2);
        }
      } else {
        setStep(2); // Advance to payment step
      }

    } catch (error) {
      console.error('Erro ao iniciar a venda:', error);
      showToast(error.message || 'Não foi possível iniciar a compra. Tente novamente.', 'error');
    } finally {
      setProcessing(false);
    }
  };

  // Processa pagamento
  const handleProcessPayment = async () => {
    setProcessing(true)

    try {
      const totalCents = Math.round(getTotal() * 100);
      const balanceToUseCents = userBalanceCents > 0 ? Math.min(userBalanceCents, totalCents) : 0;
      const remainingCents = totalCents - balanceToUseCents;

      const payments = [];

      if (balanceToUseCents > 0) {
        payments.push({
          type: "Balance",
          amount: balanceToUseCents
        });
      }

      if (remainingCents > 0) {
        const method = PAYMENT_METHODS.find(m => m.id === paymentMethod);
        const paymentPayload = {
          type: method.type,
          amount: remainingCents
        };

        if (method.type === 'CreditCard') {
          // Validação básica de cartão
          const rawCardNumber = cardData.number.replace(/\D/g, '');
          if (!rawCardNumber || rawCardNumber.length < 13) {
            throw new Error('Número do cartão inválido.');
          }
          if (!cardData.name || cardData.name.length < 3) {
            throw new Error('Nome no cartão inválido.');
          }
          if (!validateCardExpiry(cardData.expiry)) {
            throw new Error('Validade do cartão inválida. Use o formato MM/AAAA (ex: 12/2026).');
          }
          if (!cardData.cvv || cardData.cvv.length < 3) {
            throw new Error('CVV inválido.');
          }
          if (!cardData.brand) {
            throw new Error('Selecione a bandeira do cartão.');
          }

          paymentPayload.credit_card = {
            card_number: cardData.number.replace(/\D/g, ''),
            holder: cardData.name,
            expiration_date: cardData.expiry, // Expected mm/yyyy
            security_code: cardData.cvv,
            brand: cardData.brand
          };
        }

        payments.push(paymentPayload);
      } else if (remainingCents < 0) {
        throw new Error('Valor de saldo superior ao total da compra.');
      }

      const response = await api.post(`/sales/${saleId}/payment`, payments);

      if (response.status === 'COMPLETED') {
        if (balanceToUseCents >= totalCents && totalCents > 0) {
          setIsFullBalancePurchase(true);
        }
        handleSuccess(response);
      } else if (paymentMethod === 'pix' && response.qr_code) {
        setPixData({
          qr_code: response.qr_code,
          qr_code_base64: response.qr_code_base64
        });
        setShowPixModal(true);
      } else if (paymentMethod === 'credit' && response.status !== 'COMPLETED') {
        // Trata caso de cartão de crédito não aprovado ou pendente
        const errorMsg = response.message || 'Transação não finalizada. Verifique os dados do cartão e tente novamente.';
        throw new Error(errorMsg);
      } else {
        // Se não for pix/cartão esperado e não estiver COMPLETED
        console.warn('Status inesperado:', response.status);
        if (response.status === 'FAILED_PAYMENT' || response.status === 'CANCELLED') {
          navigate(`/${city?.slug || ''}/transacao-erro`, { state: { transactionId: saleId } });
        }
      }
    } catch (error) {
      console.error('Erro no pagamento:', error);
      showToast(error.message || 'Erro ao processar pagamento.', 'error');
    } finally {
      setProcessing(false)
    }
  }

  // Avança para próximo passo
  const handleNextStep = () => {
    if (step === 1) {
      startSale(); // Call startSale to initiate the payment process
    } else if (step === 2) {
      handleProcessPayment();
    }
  }

  // Polling automático com React Query para status do Pix (pausa em background, refetch automático imediato no foco)
  const { data: pixStatusData } = useQuery({
    queryKey: ['sale_status', saleId],
    queryFn: async () => {
      if (!saleId) return null;
      return await api.get(`/sales/${saleId}/status`);
    },
    enabled: Boolean(saleId) && showPixModal && timeLeft > 0,
    refetchInterval: (query) => {
      const status = query?.state?.data?.status;
      if (status === 'COMPLETED' || status === 'CANCELLED' || status === 'FAILED_PAYMENT') {
        return false;
      }
      return 15000;
    },
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (!pixStatusData || !showPixModal) return;

    if (pixStatusData.status === 'COMPLETED') {
      setShowPixModal(false);
      handleSuccess(pixStatusData);
    } else if (pixStatusData.status === 'CANCELLED' || pixStatusData.status === 'FAILED_PAYMENT') {
      setShowPixModal(false);
      navigate(`/${city?.slug || ''}/transacao-erro`, { state: { transactionId: saleId } });
    }
  }, [pixStatusData, showPixModal, handleSuccess, navigate, city?.slug, saleId]);

  if (authLoading || (isAuthenticated && loading)) {
    return (
      <div className="checkout-page checkout-page--loading">
        <Loading size="lg" text="Carregando checkout..." />
      </div>
    )
  }

  if (!isAuthenticated) {
    return null
  }

  if (!event) { // Only check if event data is not found
    return (
      <div className="checkout-page checkout-page--empty">
        <h1>Evento não encontrado</h1>
        <p>O evento que você está procurando não existe ou foi removido.</p>
        <Link to={`/${city?.slug || ''}/evento/${eventId}`}>
          <Button variant="primary">Voltar para o evento</Button>
        </Link>
      </div>
    )
  }

  return (
    <main className="checkout-page">
      <div className="checkout-page__container container">
        {/* Progress */}
        <div className="checkout-page__progress">
          <div className={`checkout-page__progress-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
            <span className="checkout-page__progress-number">1</span>
            <span className="checkout-page__progress-label">Participantes</span>
          </div>
          <div className="checkout-page__progress-line"></div>
          <div className={`checkout-page__progress-step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
            <span className="checkout-page__progress-number">2</span>
            <span className="checkout-page__progress-label">Pagamento</span>
          </div>
          <div className="checkout-page__progress-line"></div>
          <div className={`checkout-page__progress-step ${step >= 3 ? 'active' : ''}`}>
            <span className="checkout-page__progress-number">3</span>
            <span className="checkout-page__progress-label">Confirmação</span>
          </div>
        </div>

        <div className={`checkout-page__layout ${step === 3 ? 'checkout-page__layout--confirmation' : ''}`}>
          {/* Main Content */}
          <div className="checkout-page__main">
            {/* Step 1: Participantes */}
            {step === 1 && (
              <div className="checkout-page__step">
                <h2 className="checkout-page__step-title">Dados dos Participantes</h2>
                <p className="checkout-page__step-subtitle">
                  Informe a quantidade desejada e o CPF da pessoa maior de 18 anos que utilizará o outro ingresso.
                </p>

                <div className="checkout-page__field">
                  <label>Quantidade de Ingressos</label>
                  <div className="checkout-page__ticket-quantity">
                    <button
                      className="checkout-page__quantity-btn"
                      onClick={() => setTotalTicketsToPurchase(prev => Math.max(1, prev - 1))}
                      disabled={totalTicketsToPurchase <= 1}
                    >
                      −
                    </button>
                    <span className="checkout-page__quantity-value">
                      {totalTicketsToPurchase}
                    </span>
                    <button
                      className="checkout-page__quantity-btn"
                      onClick={() => setTotalTicketsToPurchase(prev => prev < maxAllowed ? prev + 1 : prev)}
                      disabled={maxAllowed === undefined || totalTicketsToPurchase >= maxAllowed}
                    >
                      +
                    </button>
                  </div>
                </div>

                {participantErrors.ticketAssignment && (
                  <div className="checkout-page__error-box">
                    <span className="checkout-page__field-error">
                      {participantErrors.ticketAssignment}
                    </span>
                  </div>
                )}

                <div className="checkout-page__participants">
                  {participants.map((participant, index) => {
                    return (
                      <div key={participant.id} className="checkout-page__participant">
                        <div className="checkout-page__participant-header">
                          <h3>
                            Ingresso {index + 1}
                            {participant.isOwner && <Badge variant="primary" size="sm">Você</Badge>}
                          </h3>
                          {!ENFORCE_FIRST_TICKET_OWNER && user && (
                            <Button
                              variant={participant.isOwner ? "secondary" : "outline"} // Change variant based on selection
                              size="sm"
                              onClick={() => handleParticipantChange(index, 'isOwner', !participant.isOwner)}
                              className="checkout-page__owner-toggle-btn"
                            >
                              {participant.isOwner ? "Não sou eu" : "Esse ingresso é para mim"}
                            </Button>
                          )}
                          <span className="checkout-page__participant-price">
                            {formatCurrency(participant.ticketPrice)}
                          </span>
                        </div>

                        <div className="checkout-page__participant-form">
                          <Dropdown
                            label="Tipo de Ingresso"
                            id={`ticket-type-${index}`}
                            name="ticketId"
                            value={participant.ticketId || ''}
                            onChange={(e) => handleParticipantChange(index, 'ticketId', e.target.value)}
                            options={[
                              { value: "", label: "Selecione um tipo de ingresso" },
                              ...sortedTicketTypes.map(ticketType => ({
                                value: ticketType.id,
                                label: `${ticketType.name} - ${formatCurrency(ticketType.value_cents / 100)}`
                              }))
                            ]}
                            error={participantErrors[`${index}-ticketId`]}
                          />
                          {participant.ticketId && event.ticket_types.find(t => t.id === participant.ticketId)?.description && (
                            <span className="checkout-page__field-hint">
                              {event.ticket_types.find(t => t.id === participant.ticketId).description}
                            </span>
                          )}

                          <div className="checkout-page__row">
                            <Dropdown
                              label="Tipo de Documento"
                              id={`document-type-${index}`}
                              name="documentType"
                              value={participant.documentType}
                              onChange={(e) => handleParticipantChange(index, 'documentType', e.target.value)}
                              options={DISABLE_PASSPORT_REGISTRATION && participant.documentType !== 'Passaporte' ? DOCUMENT_TYPES.filter(t => t.value !== 'Passaporte') : DOCUMENT_TYPES}
                              disabled={participant.isOwner || DISABLE_PASSPORT_REGISTRATION}
                              error={participantErrors[`${index}-documentType`]}
                            />

                            <div className="checkout-page__field">
                              <label>Documento</label>
                              <input
                                type="text"
                                value={participant.documentType?.toUpperCase() === 'CPF' ? formatCPF(participant.document) : participant.document}
                                onChange={(e) => handleParticipantChange(index, 'document', e.target.value)}
                                placeholder={participant.documentType?.toUpperCase() === 'CPF' ? '000.000.000-00' : 'Número do passaporte'}
                                className={participantErrors[`${index}-document`] ? 'input--error' : ''}
                                disabled={participant.isOwner || participant.verificationStatus === 'verifying'}
                              />
                              {participantErrors[`${index}-document`] && (
                                <span className="checkout-page__field-error">
                                  {participantErrors[`${index}-document`]}
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="checkout-page__field">
                            <label>Nome completo</label>
                            <input
                              type="text"
                              value={participant.name}
                              onChange={(e) => handleParticipantChange(index, 'name', e.target.value)}
                              placeholder="Nome como no documento"
                              className={participantErrors[`${index}-name`] ? 'input--error' : ''}
                              disabled={participant.documentType?.toUpperCase() === 'CPF' || participant.isOwner || participant.verificationStatus === 'verifying'}
                            />
                            {participantErrors[`${index}-name`] && (
                              <span className="checkout-page__field-error">
                                {participantErrors[`${index}-name`]}
                              </span>
                            )}
                          </div>

                          <div className="checkout-page__field">
                            <label>Data de nascimento</label>
                            <DatePicker
                              selected={parseDayMonthYearToDate(participant.dob)} onChange={(date) => {
                                const formattedDate = date ? formatDateToDayMonthYear(date) : '';
                                handleParticipantChange(index, 'dob', formattedDate);
                              }}
                              dateFormat="dd/MM/yyyy"
                              locale="pt-BR"
                              placeholderText="dd/mm/aaaa"
                              showYearDropdown
                              scrollableYearDropdown
                              yearDropdownItemNumber={80}
                              maxDate={new Date()}
                              peekNextMonth
                              showMonthDropdown
                              dropdownMode="select"
                              shouldCloseOnSelect
                              disabled={(participant.isOwner && !!user?.birth_date) || (participant.documentType?.toUpperCase() === 'CPF' && !participant.isOwner) || participant.verificationStatus === 'verifying'}
                              customInput={
                                <DatePickerInput
                                  className={participantErrors[`${index}-dob`] ? 'input--error' : ''}
                                  value={participant.dob}
                                />
                              }
                            />
                            {participantErrors[`${index}-dob`] && (
                              <span className="checkout-page__field-error">
                                {participantErrors[`${index}-dob`]}
                              </span>
                            )}
                          </div>



                          {/* --- VERIFICATION UI --- */}
                          {participant.verificationStatus !== 'pending' && (
                            <div className={`checkout-page__verification-status checkout-page__verification-status--${participant.verificationStatus}`}>
                              {participant.verificationStatus === 'verifying' && (
                                <Loading size="sm" />
                              )}
                              {participant.verificationStatus === 'success' && (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12" /></svg>
                              )}
                              {participant.verificationStatus === 'error' && (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>
                              )}
                              <span>{participant.verificationMessage}</span>
                            </div>
                          )}

                          {participant.verificationStatus === 'success' && (
                            <p className="checkout-page__signup-incentive">
                              Para ter acesso ao seu ingresso, basta se cadastrar e acessar sua conta no Arqzin.
                            </p>
                          )}


                        </div>
                      </div>
                    )
                  })}
                </div>

                {extraQuestions.length > 0 && (
                  <div className="checkout-page__extra-questions">
                    <h3 className="checkout-page__step-subtitle" style={{ marginTop: '2rem', marginBottom: '0.75rem' }}>Perguntas Adicionais</h3>
                    <div className="checkout-page__participant">
                      <div className="checkout-page__participant-form">
                        {extraQuestions.map(q => {
                          const qId = q.event_question_id || q.question_id;
                          return (
                            <div key={qId} className="checkout-page__field">
                              {q.question_type === 'DROPDOWN' ? (
                                <Dropdown
                                  label={q.question}
                                  id={qId}
                                  value={q.answer || ''}
                                  onChange={(e) => handleQuestionAnswerChange(qId, e.target.value)}
                                  options={[
                                    { value: '', label: 'Selecione uma opção' },
                                    ...(q.question_options?.options || []).map(opt => ({
                                      value: opt,
                                      label: opt
                                    }))
                                  ]}
                                  error={q.error}
                                />
                              ) : (
                                <>
                                  <label>{q.question}</label>
                                  <input
                                    type="text"
                                    value={q.answer || ''}
                                    onChange={(e) => handleQuestionAnswerChange(qId, e.target.value)}
                                    placeholder="Sua resposta"
                                    className={q.error ? 'input--error' : ''}
                                  />
                                  {q.error && (
                                    <span className="checkout-page__field-error">{q.error}</span>
                                  )}
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                <div className="checkout-page__info-box">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
                  </svg>
                  <div>
                    <strong>Importante:</strong> os ingressos são pessoais e intransferíveis.
                    O reembolso pode ser solicitado até o horário de início do evento, diretamente pela carteira do cliente na plataforma.
                    <br /><br />Ah, e para entrar é obrigatória a apresentação de um documento oficial com foto.
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Pagamento */}
            {step === 2 && (
              <div className="checkout-page__step">
                <h2 className="checkout-page__step-title">Finalize sua Compra</h2>
                <p className="checkout-page__step-subtitle">
                  Você tem 5 minutos para concluir o pagamento.
                </p>

                {saleId && expiresAt && (
                  <div className="checkout-page__countdown">
                    <div className="checkout-page__countdown-timer">
                      <span className="checkout-page__countdown-icon">⏳</span>
                      <span className="checkout-page__countdown-value">{formatTime(timeLeft)}</span>
                    </div>
                    <p className="checkout-page__countdown-message">
                      Sua reserva de ingressos (ID: <strong>{saleId}</strong>) expira em
                      {` ${formatTime(timeLeft)} `}
                      minutos.
                    </p>
                  </div>
                )}

                {/* Saldo da Carteira (Obrigatório e Pré-selecionado) */}
                {userBalanceCents > 0 && (
                  <div className="checkout-page__balance-section">
                    <div className="checkout-page__balance-card">
                      <div className="checkout-page__balance-card-header">
                        <div className="checkout-page__balance-card-title-group">
                          <span className="checkout-page__balance-card-badge">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                            Saldo Aplicado
                          </span>
                          <span className="checkout-page__balance-card-title">Créditos da Carteira</span>
                        </div>
                        <span className="checkout-page__balance-card-amount">
                          -{formatCurrency(Math.min(userBalance, getTotal()))}
                        </span>
                      </div>
                      <p className="checkout-page__balance-card-desc">
                        Seu saldo disponível de <strong>{formatCurrency(userBalance)}</strong> foi aplicado automaticamente neste pedido.
                      </p>
                    </div>
                  </div>
                )}

                {/* Se cobrir 100% com saldo */}
                {userBalance >= getTotal() && getTotal() > 0 && (
                  <div className="checkout-page__info-box checkout-page__info-box--success" style={{ marginTop: '1.5rem' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                      <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                    <div>
                      <strong>Pagamento 100% com Saldo</strong>
                      <p>Seu saldo cobre o valor total da compra. Basta clicar em "Finalizar compra".</p>
                    </div>
                  </div>
                )}

                {/* Forma de Pagamento do Valor Restante */}
                {(getTotal() - (userBalanceCents > 0 ? Math.min(userBalance, getTotal()) : 0)) > 0 && (
                  <>
                    {userBalanceCents > 0 ? (
                      <div className="checkout-page__remaining-alert">
                        <div className="checkout-page__remaining-alert-icon">💳</div>
                        <div className="checkout-page__remaining-alert-content">
                          <span className="checkout-page__remaining-alert-subtitle">Valor restante a pagar</span>
                          <strong className="checkout-page__remaining-alert-value">
                            {formatCurrency(getTotal() - Math.min(userBalance, getTotal()))}
                          </strong>
                          <p className="checkout-page__remaining-alert-hint">
                            Escolha como deseja pagar o valor restante:
                          </p>
                        </div>
                      </div>
                    ) : (
                      <>
                        <h3 className="checkout-page__step-subtitle" style={{ marginTop: '2rem' }}>Forma de Pagamento</h3>
                        <p className="checkout-page__step-subtitle">
                          Escolha como deseja pagar o valor total de <strong>{formatCurrency(getTotal())}</strong>
                        </p>
                      </>
                    )}

                    <div className="checkout-page__payment-methods">
                      {PAYMENT_METHODS.filter(m => m.id !== 'balance').map(method => (
                        <button
                          key={method.id}
                          className={`checkout-page__payment-method ${paymentMethod === method.id ? 'active' : ''}`}
                          onClick={() => setPaymentMethod(method.id)}
                        >
                          <span className="checkout-page__payment-icon">{method.icon}</span>
                          <div className="checkout-page__payment-info">
                            <span className="checkout-page__payment-name">{method.name}</span>
                            <span className="checkout-page__payment-desc">{method.description}</span>
                          </div>
                          <div className="checkout-page__payment-check">
                            {paymentMethod === method.id && (
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>

                    {/* Formulário de cartão */}
                    {paymentMethod === 'credit' && (
                      <div className="checkout-page__card-form">
                        <Dropdown
                          label="Bandeira do cartão"
                          value={cardData.brand}
                          onChange={(e) => setCardData(prev => ({ ...prev, brand: e.target.value }))}
                          options={[
                            { value: '', label: 'Selecione a bandeira' },
                            ...CARD_BRANDS
                          ]}
                          className={!cardData.brand ? 'input--empty' : ''}
                        />

                        <div className="checkout-page__field">
                          <label>Número do cartão</label>
                          <input
                            type="text"
                            inputMode="numeric"
                            autoComplete="cc-number"
                            value={cardData.number}
                            onChange={(e) => {
                              const digits = e.target.value.replace(/\D/g, '').substring(0, 16);
                              const formatted = digits.replace(/(\d{4})(?=\d)/g, '$1 ');
                              setCardData(prev => ({ ...prev, number: formatted }));
                            }}
                            placeholder="0000 0000 0000 0000"
                            maxLength="19"
                          />
                        </div>

                        <div className="checkout-page__field">
                          <label>Nome no cartão</label>
                          <input
                            type="text"
                            value={cardData.name}
                            onChange={(e) => setCardData(prev => ({ ...prev, name: e.target.value }))}
                            placeholder="Como está no cartão"
                          />
                        </div>

                        <div className="checkout-page__row">
                          <div className="checkout-page__field">
                            <label>Validade</label>
                            <input
                              type="text"
                              value={cardData.expiry}
                              onChange={(e) => {
                                let val = e.target.value.replace(/\D/g, '');

                                // Validação básica de mês ao digitar
                                if (val.length >= 2) {
                                  const month = parseInt(val.substring(0, 2));
                                  if (month > 12) val = '12' + val.substring(2);
                                  if (month === 0 && val.length === 2) val = '01';
                                }

                                if (val.length > 2) {
                                  val = val.substring(0, 2) + '/' + val.substring(2, 6);
                                }
                                setCardData(prev => ({ ...prev, expiry: val }));
                              }}
                              placeholder="MM/AAAA"
                              maxLength="7"
                            />
                            <small className="checkout-page__field-hint">Ex: 12/2026 (ano com 4 dígitos)</small>
                          </div>

                          <div className="checkout-page__field">
                            <label>CVV</label>
                            <input
                              type="text"
                              value={cardData.cvv}
                              onChange={(e) => setCardData(prev => ({ ...prev, cvv: e.target.value.replace(/\D/g, '').substring(0, 3) }))}
                              placeholder="123"
                              maxLength="3"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Info PIX */}
                    {paymentMethod === 'pix' && (
                      <div className="checkout-page__pix-info">
                        <div className="checkout-page__info-box checkout-page__info-box--success">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                            <polyline points="22 4 12 14.01 9 11.01" />
                          </svg>
                          <div>
                            <strong>Pagamento via Pix</strong>
                            <p>Após confirmar, você receberá um código Pix para pagamento.
                              O pagamento é processado instantaneamente.</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Step 3: Confirmação */}
            {step === 3 && orderResult && (
              <div className="checkout-page__step checkout-page__step--confirmation">
                <div className="checkout-page__success-icon">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                </div>

                <h2 className="checkout-page__step-title">Compra Confirmada!</h2>
                <p className="checkout-page__step-subtitle">
                  Pedido #{orderResult.orderId}
                </p>

                {isFullBalancePurchase && (
                  <div className="checkout-page__balance-success-banner">
                    <div className="checkout-page__balance-success-badge">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                        <polyline points="22 4 12 14.01 9 11.01" />
                      </svg>
                    </div>
                    <div className="checkout-page__balance-success-text">
                      <strong>O saldo na sua carteira foi suficiente para finalizar a sua compra!!</strong>
                      <p>O valor total foi pago diretamente com os créditos disponíveis na sua conta.</p>
                    </div>
                  </div>
                )}

                <div className="checkout-page__tickets-result">
                  <h3>Seus ingressos</h3>
                  {orderResult.tickets.map((ticket, index) => (
                    <div key={index} className="checkout-page__ticket-result">
                      <div className="checkout-page__ticket-result-info">
                        <span className="checkout-page__ticket-result-name">{ticket.name}</span>
                        <span className="checkout-page__ticket-result-type">{ticket.ticketName}</span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="checkout-page__info-box">
                  {ENABLE_TICKET_EMAIL_NOTIFICATIONS ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                  ) : (
                    <FaTicketAlt size={20} />
                  )}
                  <div>
                    {ENABLE_TICKET_EMAIL_NOTIFICATIONS
                      ? "Os ingressos foram enviados para os e-mails informados. Você também pode acessá-los na sua carteira."
                      : "Você pode acessar seus ingressos na sua carteira."}
                  </div>
                </div>

                <div className="checkout-page__confirmation-actions">
                  <Link to={`/${city?.slug || ''}/carteira`}>
                    <Button variant="primary" size="lg">
                      Ver meus ingressos
                    </Button>
                  </Link>
                  <Link to={`/${city?.slug || ''}/`}>
                    <Button variant="secondary" size="lg">
                      Voltar para eventos
                    </Button>
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - Resumo */}
          {step < 3 && (
            <aside className="checkout-page__sidebar">
              <div className="checkout-page__summary">
                <h3 className="checkout-page__summary-title">Resumo do pedido</h3>

                {/* Evento */}
                <div className="checkout-page__summary-event">
                  {event?.main_image_url && <img src={event.main_image_url} alt={event?.name || 'Imagem do Evento'} />}
                  <div>
                    {event?.name && <h4>{event.name}</h4>}
                    {event?.location && <span>{event.location}</span>}
                    {event?.start_date && <span>{formatDateTime(event.start_date)}</span>}
                    {/* Fallback to event.date if start_date is not available, similar to EventCard */}
                    {!event?.start_date && event?.date && <span>{formatDateTime(event.date)}</span>}
                  </div>
                </div>

                {/* Voucher Niver */}
                <div className="checkout-page__summary-coupon">
                  {!coupon ? (
                    <div className="checkout-page__coupon-input-group">
                      <input
                        type="text"
                        placeholder="Voucher Niver"
                        value={couponInput}
                        onChange={(e) => {
                          setCouponInput(e.target.value);
                          setCouponError('');
                        }}
                        onKeyDown={(e) => e.key === 'Enter' && handleValidateCoupon()}
                        disabled={isValidatingCoupon}
                      />
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleValidateCoupon}
                        loading={isValidatingCoupon}
                        disabled={!couponInput.trim()}
                      >
                        Aplicar
                      </Button>
                    </div>
                  ) : (
                    <div className="checkout-page__coupon-applied">
                      <div className="checkout-page__coupon-info">
                        <FaTicketAlt />
                        <span>Cupom <strong>{coupon.coupon_id}</strong> aplicado</span>
                      </div>
                      <button className="checkout-page__coupon-remove" onClick={removeCoupon}>
                        Remover
                      </button>
                    </div>
                  )}
                  {couponError && <span className="checkout-page__coupon-error">{couponError}</span>}
                </div>

                {/* Beneficiário (Condicional via feature flag) */}
                {ENABLE_CHECKOUT_BENEFICIARY_SUMMARY && (
                  <div className="checkout-page__summary-provider">
                    <span className="checkout-page__summary-provider-label">Beneficiário</span>
                    <div className="checkout-page__summary-provider-info">
                      <strong>Arq Produções</strong>
                      <span>35.302.161/0001-14</span>
                    </div>
                  </div>
                )}

                {/* Ingressos */}
                <div className="checkout-page__summary-items">
                  {Object.entries(
                    participants.reduce((acc, p) => {
                      acc[p.ticketName] = acc[p.ticketName] || { count: 0, price: p.ticketPrice }
                      acc[p.ticketName].count++
                      return acc
                    }, {})
                  ).map(([name, data]) => (
                    <div key={name} className="checkout-page__summary-item">
                      <span>{data.count}x {name}</span>
                      <span>{formatCurrency(data.price * data.count)}</span>
                    </div>
                  ))}
                </div>

                {/* Totais */}
                <div className="checkout-page__summary-totals">
                  <div className="checkout-page__summary-row">
                    <span>Subtotal</span>
                    <span>{formatCurrency(participants.reduce((sum, p) => sum + p.ticketPrice, 0))}</span>
                  </div>
                  {getDiscountValue() > 0 && (
                    <div className="checkout-page__summary-row checkout-page__summary-row--discount">
                      <span>Desconto ({coupon.discount}%)</span>
                      <span>-{formatCurrency(getDiscountValue())}</span>
                    </div>
                  )}
                  {getServiceFee() > 0 && (
                    <div className="checkout-page__summary-row">
                      <span>Taxa de serviço</span>
                      <span>{formatCurrency(getServiceFee())}</span>
                    </div>
                  )}
                  {userBalanceCents > 0 && getTotal() > 0 && (
                    <div className="checkout-page__summary-row checkout-page__summary-row--balance">
                      <span className="checkout-page__summary-balance-label">
                        <span className="checkout-page__summary-balance-tag">Saldo</span>
                        Créditos da carteira
                      </span>
                      <span className="checkout-page__summary-balance-val">
                        -{formatCurrency(Math.min(userBalance, getTotal()))}
                      </span>
                    </div>
                  )}
                  <div className="checkout-page__summary-row checkout-page__summary-row--total">
                    <span>{userBalanceCents > 0 ? 'Total a pagar' : 'Total do pedido'}</span>
                    <span>{formatCurrency(Math.max(0, getTotal() - (userBalanceCents > 0 ? Math.min(userBalance, getTotal()) : 0)))}</span>
                  </div>
                </div>

                {/* Botões */}
                <div className="checkout-page__summary-actions">
                  {step === 1 && !isParticipantsFormValid() && !processing && (
                    <div className="checkout-page__summary-error">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
                      <span>{getMissingFieldsMessage()}</span>
                    </div>
                  )}
                  {step > 1 && (
                    <Button
                      variant="secondary"
                      fullWidth
                      onClick={() => setStep(step - 1)}
                    >
                      Voltar
                    </Button>
                  )}
                  <Button
                    variant="primary"
                    fullWidth
                    onClick={handleNextStep}
                    loading={processing}
                    disabled={processing || (step === 1 && !isParticipantsFormValid())}
                  >
                    {step === 1 ? 'Continuar' : 'Finalizar compra'}
                  </Button>
                </div>
              </div>
            </aside>
          )}
        </div>

        {/* Barra de ações fixa para Mobile */}
        {step < 3 && (
          <div className="checkout-page__mobile-actions">
            <div className="checkout-page__mobile-actions-content">
              <div className="checkout-page__mobile-actions-info">
                <span>{userBalanceCents > 0 ? 'Total a pagar' : 'Total'}</span>
                <strong>{formatCurrency(Math.max(0, getTotal() - (userBalanceCents > 0 ? Math.min(userBalance, getTotal()) : 0)))}</strong>
              </div>
              <div className="checkout-page__mobile-actions-buttons">
                {step > 1 && (
                  <Button
                    variant="secondary"
                    onClick={() => setStep(step - 1)}
                    disabled={processing}
                  >
                    Voltar
                  </Button>
                )}
                <Button
                  variant="primary"
                  onClick={handleNextStep}
                  loading={processing}
                  disabled={processing || (step === 1 && !isParticipantsFormValid())}
                >
                  {step === 1 ? 'Continuar' : 'Finalizar compra'}
                </Button>
              </div>
            </div>
            {step === 1 && !isParticipantsFormValid() && !processing && (
              <div className="checkout-page__mobile-actions-error">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
                <span>{getMissingFieldsMessage()}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal PIX */}
      <Modal
        isOpen={showPixModal}
        onClose={() => setShowPixModal(false)}
        title="Pagamento via Pix"
        size="md"
      >
        <div className="checkout-page__pix-modal">
          <div className="checkout-page__pix-status-msg">
            <Loading size="sm" />
            <span>Aguardando pagamento...</span>
          </div>

          {timeLeft > 0 && (
            <div className="checkout-page__pix-countdown">
              <span className="checkout-page__pix-countdown-icon">⏳</span>
              <span className="checkout-page__pix-countdown-value">{formatTime(timeLeft)}</span>
            </div>
          )}

          <div className="checkout-page__pix-qr">
            {pixData?.qr_code_base64 ? (
              <img src={`data:image/png;base64,${pixData.qr_code_base64}`} alt="QR Code Pix" />
            ) : (
              <div className="checkout-page__pix-qr-placeholder">
                <Loading size="sm" />
              </div>
            )}
          </div>

          <div className="checkout-page__pix-code">
            <label>Código Pix Copia e Cola</label>
            <div className="checkout-page__pix-code-input">
              <input type="text" value={pixData?.qr_code || ''} readOnly />
              <button onClick={async () => {
                const copied = await copyToClipboard(pixData?.qr_code || '');
                if (copied) {
                  showToast('Código Pix copiado!', 'success');
                } else {
                  showToast('Não foi possível copiar automaticamente.', 'warning');
                }
              }}>
                Copiar
              </button>
            </div>
          </div>

          <div className="checkout-page__pix-value">
            <span>Valor:</span>
            <strong>{formatCurrency(getTotal())}</strong>
          </div>

          <div className="checkout-page__pix-timer">
            <span>Aguarde nesta tela até a confirmação automática.</span>
          </div>
        </div>
      </Modal>

      {/* Modal de Venda Existente */}
      <Modal
        isOpen={showExistingSaleModal}
        onClose={() => setShowExistingSaleModal(false)}
        title="Venda Pendente"
        size="md"
      >
        <div className="checkout-page__existing-sale-modal">
          <div className="checkout-page__info-box checkout-page__info-box--warning">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <div>
              <strong>Atenção:</strong> Você já possui uma transação em andamento para outro evento.
            </div>
          </div>

          <p className="checkout-page__modal-text">
            Não é possível realizar duas compras simultaneamente. O que deseja fazer?
          </p>

          <div className="checkout-page__modal-actions">
            <Button
              variant="secondary"
              fullWidth
              onClick={handleResumeExistingSale}
            >
              Voltar para a transação anterior
            </Button>
            <Button
              variant="outline"
              fullWidth
              onClick={handleDiscardExistingSale}
            >
              Cancelar anterior e começar nova
            </Button>
          </div>
        </div>
      </Modal>
    </main >
  )
}

export default Checkout
